import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  DatePicker,
  Tag,
  Popconfirm,
  Space,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const { Title, Text } = Typography;

interface PromotionCode {
  id: string;
  code: string;
  description: string | null;
  leadsCapOverride: number | null;
  usersCapOverride: number | null;
  grantsCrm: boolean;
  grantsSuperAdmin: boolean;
  trialDays: number | null;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
}

interface FormShape {
  code: string;
  description?: string;
  leadsCapOverride?: number | null;
  usersCapOverride?: number | null;
  grantsCrm?: boolean;
  grantsSuperAdmin?: boolean;
  trialDays?: number | null;
  maxUses?: number | null;
  expiresAt?: Dayjs | null;
  isActive?: boolean;
}

const PromotionCodesAdminPage: React.FC = () => {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ['promotion-codes'],
    queryFn: async (): Promise<PromotionCode[]> => {
      const r = await apiClient.get<{ success: boolean; data: PromotionCode[] }>('/promotion-codes');
      return r.data.data;
    },
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromotionCode | null>(null);
  const [form] = Form.useForm<FormShape>();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, grantsCrm: false, grantsSuperAdmin: false });
    setModalOpen(true);
  };

  const openEdit = (row: PromotionCode) => {
    setEditing(row);
    form.setFieldsValue({
      code: row.code,
      description: row.description ?? undefined,
      leadsCapOverride: row.leadsCapOverride,
      usersCapOverride: row.usersCapOverride,
      grantsCrm: row.grantsCrm,
      grantsSuperAdmin: row.grantsSuperAdmin,
      trialDays: row.trialDays,
      maxUses: row.maxUses,
      expiresAt: row.expiresAt ? dayjs(row.expiresAt) : null,
      isActive: row.isActive,
    });
    setModalOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: async (values: FormShape) => {
      const payload = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      };
      if (editing) {
        await apiClient.put(`/promotion-codes/${editing.id}`, payload);
      } else {
        await apiClient.post('/promotion-codes', payload);
      }
    },
    onSuccess: () => {
      message.success(editing ? 'Code updated' : 'Code created');
      qc.invalidateQueries({ queryKey: ['promotion-codes'] });
      setModalOpen(false);
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message || 'Failed to save promotion code'),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/promotion-codes/${id}`);
    },
    onSuccess: () => {
      message.success('Code deleted');
      qc.invalidateQueries({ queryKey: ['promotion-codes'] });
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      saveMut.mutate(values);
    } catch {
      // antd already shows field errors
    }
  };

  const statusOf = (row: PromotionCode): { color: string; label: string } => {
    if (!row.isActive) return { color: 'default', label: 'Disabled' };
    if (row.expiresAt && dayjs(row.expiresAt).isBefore(dayjs())) return { color: 'default', label: 'Expired' };
    if (row.maxUses !== null && row.usedCount >= (row.maxUses ?? 0))
      return { color: 'default', label: 'Used up' };
    return { color: 'green', label: 'Active' };
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (c: string) => <Text code strong>{c}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (d: string | null) => d ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Overrides',
      key: 'overrides',
      render: (_: unknown, row: PromotionCode) => (
        <Space wrap size={[4, 4]}>
          {row.leadsCapOverride !== null && <Tag>leads: {row.leadsCapOverride}</Tag>}
          {row.usersCapOverride !== null && <Tag>users: {row.usersCapOverride}</Tag>}
          {row.grantsCrm && <Tag color="blue">CRM</Tag>}
          {row.grantsSuperAdmin && <Tag color="purple">SuperAdmin</Tag>}
          {row.trialDays && <Tag color="gold">{row.trialDays}d trial</Tag>}
        </Space>
      ),
    },
    {
      title: 'Uses',
      key: 'uses',
      render: (_: unknown, row: PromotionCode) =>
        `${row.usedCount}/${row.maxUses === null ? '∞' : row.maxUses}`,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, row: PromotionCode) => {
        const s = statusOf(row);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row: PromotionCode) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
          <Popconfirm
            title={`Delete "${row.code}"?`}
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMut.mutate(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title level={4} style={{ marginBottom: 4 }}>Promotion Codes</Title>
              <Text type="secondary">
                Sales-friendly codes that override the plan at signup. Each code can grant extra
                leads, more users, CRM access, SuperAdmin privilege, or a free trial.
              </Text>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New code
            </Button>
          </div>

          <Table<PromotionCode>
            rowKey="id"
            columns={columns as any}
            dataSource={listQ.data ?? []}
            loading={listQ.isLoading}
            pagination={false}
          />
        </Space>
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? `Edit promotion code` : 'New promotion code'}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Save"
        confirmLoading={saveMut.isPending}
        width={560}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: 'Required' }, { max: 30 }]}
          >
            <Input placeholder="LAUNCH50" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Launch promo — Q3" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="leadsCapOverride" label="Leads cap" style={{ flex: 1 }}>
              <InputNumber min={0} placeholder="Plan default" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="usersCapOverride" label="User seats" style={{ flex: 1 }}>
              <InputNumber min={0} placeholder="Plan default" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="trialDays" label="Trial days" style={{ flex: 1 }}>
              <InputNumber min={0} placeholder="None" style={{ width: 140 }} />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="grantsCrm" label="Grant CRM" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="grantsSuperAdmin" label="Grant SuperAdmin" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="maxUses" label="Max uses" style={{ flex: 1 }}>
              <InputNumber min={0} placeholder="∞" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="expiresAt" label="Expires at" style={{ flex: 1 }}>
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default PromotionCodesAdminPage;
